import { useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { useLicenseSavedViews } from '../../../hooks/useLicenseSavedViews';
import type { LicenseFilterState } from '../../../types/manageLicenses';

interface Props {
  filters: LicenseFilterState;
  onApply: (filters: LicenseFilterState) => void;
}

export default function LicenseSavedViews({ filters, onApply }: Props) {
  const { t } = useTranslation();
  const { views, save, remove } = useLicenseSavedViews();
  const [name, setName] = useState('');
  const [duplicateError, setDuplicateError] = useState(false);

  const userViews = views.filter(v => !v.system);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    const isDuplicate = userViews.some(
      v => v.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (isDuplicate) {
      setDuplicateError(true);
      return;
    }

    setDuplicateError(false);
    const result = save(trimmed, filters);
    if (result !== null) {
      setName('');
    }
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (duplicateError) setDuplicateError(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
      <span className="text-neutral-600">{t('App.Admin.Licenses.LicenseSavedViews.Label')}</span>

      {views.length === 0 && <span className="text-neutral-400">{t('App.Admin.Licenses.LicenseSavedViews.None')}</span>}

      {views.map(v => (
        <span key={v.id} className="inline-flex items-center border border-neutral-300 rounded">
          <button
            type="button"
            onClick={() => onApply(v.filters)}
            className="px-2 py-1 hover:bg-neutral-100"
          >
            {v.name}
            {v.system && (
              <span className="ml-1 text-xs text-neutral-500">{t('App.Admin.Licenses.LicenseSavedViews.SystemBadge')}</span>
            )}
          </button>
          {!v.system && (
            <button
              type="button"
              onClick={() => remove(v.id)}
              className="px-1 py-1 text-neutral-400 hover:text-red-600"
              aria-label={t('App.Admin.Licenses.LicenseSavedViews.DeleteAriaLabel', { name: v.name })}
            >
              ×
            </button>
          )}
        </span>
      ))}

      <div className="flex items-center gap-1">
        <input
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder={t('App.Admin.Licenses.LicenseSavedViews.SavePlaceholder')}
          className={`border rounded px-2 py-1 text-sm w-44 ${
            duplicateError ? 'border-red-400' : 'border-neutral-300'
          }`}
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={!name.trim()}
          className="px-2 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-100 disabled:opacity-50"
        >
          {t('App.Admin.Licenses.LicenseSavedViews.SaveButton')}
        </button>
      </div>

      {duplicateError && (
        <span className="text-xs text-red-600 w-full">
          {t('App.Admin.Licenses.LicenseSavedViews.DuplicateError')}
        </span>
      )}
    </div>
  );
}
